import React, { memo, useState, useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import Grid from '@material-ui/core/Grid'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import ButtonBase from '@material-ui/core/ButtonBase'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useLocation, useHistory } from 'react-router-dom'

import styles from './styles'
import { StoreValue, HandleCompanyStory } from 'store'
import { HeaderStory, ContentStory } from './components'
import { StoryProvider, StoryStateValue } from './reducer'
import { EArticleActionTypes } from 'models'
import { useMutation } from '@apollo/react-hooks'

const useStyles = makeStyles(styles)

const CreateStoryView: React.FunctionComponent<any> = memo(props => {
	const {
		title,
		id,
		featuredImage,
		featuredVideo,
		storeUserId,
		companyStoryDispatch,
		body,
		companyId,
	} = props
	// TODO: temporary solution.
	// if featured image is comming from server I need to remove the __typename property added by apollo client.
	if (featuredImage) delete featuredImage.__typename
	const history = useHistory()
	const classes = useStyles()
	const [loading, toggle] = useState(false)
	const { t, i18n } = useTranslation()
	const [handleCompanyStory] = useMutation(HandleCompanyStory)

	const publishStory = useCallback(() => {
		let error = false
		let errorData = {
			title: false,
			body: false,
			featuredImage: false,
		}
		if (!title || title.trim() === '') {
			error = true
			errorData.title = true
		}
		if (!body || body.trim() === '') {
			error = true
			errorData.body = true
		}

		if (!featuredImage && !featuredVideo) {
			error = true
			errorData.featuredImage = true
		}
		if (error)
			companyStoryDispatch({
				type: EArticleActionTypes.setError,
				error: errorData,
			})

		if (!error) toggle(true)
	}, [body, companyStoryDispatch, featuredImage, featuredVideo, title])

	useEffect(() => {
		if (loading) {
			const mutate = async () => {
				try {
					await handleCompanyStory({
						variables: {
							story: {
								postingCompanyId: companyId,
								id,
								title,
								body,
								featuredImage,
								featuredVideo,
							},
						},
					})
					history.goBack()
				} catch (error) {
					console.error(error)
					toggle(false)
				}
			}
			mutate()
		}
	}, [
		loading,
		body,
		handleCompanyStory,
		featuredImage,
		featuredVideo,
		history,
		i18n.language,
		id,
		storeUserId,
		title,
		companyId,
	])

	return (
		<Grid
			container
			justify="center"
			classes={{ container: classes.flexGrow }}
		>
			<div className={classNames(classes.root)}>
				<HeaderStory />
				<Grid
					item
					xs={12}
					sm={8}
					md={6}
					lg={4}
					className={classNames(classes.storyContainer)}
					style={{height: 'calc(100% - 108px)'}}
				>
					<ContentStory />
				</Grid>
				<ButtonBase
					className={classes.publishButton}
					onClick={publishStory}
				>
					{!loading && t('Publish story')}
					{loading && (
						<CircularProgress
							className={classes.loading}
							size={20}
						/>
					)}
				</ButtonBase>
			</div>
		</Grid>
	)
})

const CreateStoryWithState = (props: any) => (
	<StoryStateValue
		keys={[
			'id',
			'title',
			'featuredImage',
			'featuredVideo',
			'body',
			'companyStoryDispatch',
			'error',
		]}
	>
		<CreateStoryView {...props} />
	</StoryStateValue>
)

const CreateStoryWithStoreValue = (props: any) => (
	<StoreValue keys={['user.id|storeUserId']}>
		<CreateStoryWithState {...props} />
	</StoreValue>
)

const CreateStory = (props: any) => {
	const { state }: { state: any } = useLocation()
	return (
		<StoryProvider
			story={(state && state.article) || null}
			reset={state && state.reset}
		>
			<CreateStoryWithStoreValue companyId={state.companyId} {...props} />
		</StoryProvider>
	)
}

export default CreateStory
